import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Session state interface
 */
interface SessionState {
  sessionId: string | null;
  orderId: string | null;
  token: string | null;
  branchId: string | null;
  expiresAt: string | null;
}

/**
 * Session store interface
 */
interface SessionStore extends SessionState {
  setSession: (session: {
    sessionId: string;
    orderId?: string;
    token: string;
    branchId: string;
    expiresAt: string;
  }) => void;
  setOrderId: (orderId: string) => void;
  clearSession: () => void;
  isSessionValid: () => boolean;
}

/**
 * Session Store (Zustand + localStorage persistence)
 * Manages customer session state across page refreshes
 */
export const useSessionStore = create<SessionStore>()(
  persist(
    (set, get) => ({
      // Initial state
      sessionId: null,
      orderId: null,
      token: null,
      branchId: null,
      expiresAt: null,

      // Set session data
      setSession: (session) => {
        set({
          sessionId: session.sessionId,
          orderId: session.orderId || null,
          token: session.token,
          branchId: session.branchId,
          expiresAt: session.expiresAt,
        });
      },

      // Set order ID
      setOrderId: (orderId: string) => {
        set({ orderId });
      },

      // Clear session
      clearSession: () => {
        set({
          sessionId: null,
          orderId: null,
          token: null,
          branchId: null,
          expiresAt: null,
        });
      },

      // Check if session is valid (exists and not expired)
      isSessionValid: () => {
        const { sessionId, expiresAt } = get();
        if (!sessionId || !expiresAt) {
          return false;
        }
        const expirationTime = new Date(expiresAt).getTime();
        const now = new Date().getTime();
        return now < expirationTime;
      },
    }),
    {
      name: 'customer-session', // localStorage key
      partialize: (state) => ({
        sessionId: state.sessionId,
        orderId: state.orderId,
        token: state.token,
        branchId: state.branchId,
        expiresAt: state.expiresAt,
      }),
    }
  )
);
